import { themeTokens, type ThemeName } from './tokens';

// Helper to get alpha hex
const alpha = (hex: string, alpha: number): string => {
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alphaHex}`;
};

// Helper to create rgba string
const rgba = (r: number, g: number, b: number, a: number): string => {
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Semantic colors map raw color tokens to their functional meaning
export const semanticColors = {
  light: {
    // Backgrounds
    background: themeTokens.colors.light.background,
    surface: themeTokens.colors.light.surface,
    surfaceTransparent: alpha(themeTokens.colors.light.surface, 0.8),
    surfaceHover: themeTokens.colors.light.surfaceAlt,
    transparentBackground: themeTokens.colors.light.transparentBackground,
    transparentBackgroundHover: themeTokens.colors.light.transparentBackgroundHover,

    // Cards
    cardBackground: rgba(255, 255, 255, 0.8),
    cardBackgroundHover: rgba(255, 255, 255, 0.9),
    codeBackground: rgba(249, 250, 251, 0.8),
    menuBackground: rgba(0, 0, 0, 0.8),
    menuHover: rgba(0, 0, 0, 0.9),

    // Text Hierarchy
    textHeading: themeTokens.colors.light.textHeading,
    textBody: themeTokens.colors.light.textBody,
    textDescription: themeTokens.colors.light.textDescription,
    textDisabled: themeTokens.colors.light.textDisabled,
    textLink: themeTokens.colors.light.textLink,
    textError: themeTokens.colors.light.textError,

    // Legacy Text (for backward compatibility)
    textPrimary: themeTokens.colors.light.text,
    textSecondary: themeTokens.colors.light.textSecondary,
    textMuted: themeTokens.colors.light.textMuted,
    textBrand: themeTokens.colors.light.brand,
    textBrandHover: themeTokens.colors.light.brandHover,

    // Interactive
    actionDefault: themeTokens.colors.light.textMuted,
    actionHover: themeTokens.colors.light.text,
    accent: themeTokens.colors.light.accent,
    accentHover: themeTokens.colors.light.accentHover,

    // Action Menu
    actionMenu: {
      background: themeTokens.colors.light.actionMenuBackground,
      icon: themeTokens.colors.light.textSecondary,
      iconHover: themeTokens.colors.light.text,
      useAction: themeTokens.colors.light.actionMenuUseHover,
      openAction: themeTokens.colors.light.actionMenuOpenHover,
      editAction: themeTokens.colors.light.actionMenuEditHover,
      deleteAction: themeTokens.colors.light.actionMenuDeleteHover,
    },

    // Buttons
    buttonPrimary: themeTokens.colors.light.buttonPrimary,
    buttonPrimaryHover: themeTokens.colors.light.buttonPrimaryHover,
    buttonPrimaryText: themeTokens.colors.light.buttonPrimaryText,
    buttonSecondary: themeTokens.colors.light.buttonSecondary,
    buttonSecondaryHover: themeTokens.colors.light.buttonSecondaryHover,
    buttonSecondaryText: themeTokens.colors.light.buttonSecondaryText,
    buttonDisabled: themeTokens.colors.light.buttonDisabled,

    // Borders
    border: themeTokens.colors.light.border,
    borderTransparent: alpha(themeTokens.colors.light.border, 0.5),

    // Special
    folderIcon: '#000000', // Black for light mode,

    // Settings
    settings: {
      background: themeTokens.colors.light.background,
      surfacePrimary: themeTokens.colors.light.surface,
      surfaceSecondary: themeTokens.colors.light.surfaceAlt,
      inputBackground: themeTokens.colors.light.surfaceAlt,
      inputBorder: themeTokens.colors.light.border,
      inputText: themeTokens.colors.light.text,
      tabBackground: themeTokens.colors.light.surfaceAlt,
      tabHover: themeTokens.colors.light.surface,
      tabActive: themeTokens.colors.light.accent,
      tabBorder: themeTokens.colors.light.border,
      text: themeTokens.colors.light.settingsText,
      description: themeTokens.colors.light.settingsTextSecondary,
      toggleEnabled: themeTokens.colors.light.toggleEnabled,
      toggleDisabled: themeTokens.colors.light.toggleDisabled,
      toggleBorder: themeTokens.colors.light.toggleBorder,
      toggleRing: themeTokens.colors.light.toggleRing,
    },

    // Forms
    form: {
      background: themeTokens.colors.light.background,
      modalOverlay: alpha(themeTokens.colors.light.background, 0.8),
      cardBackground: themeTokens.colors.light.surface,
      inputBackground: themeTokens.colors.light.surfaceAlt,
      inputBorder: themeTokens.colors.light.border,
      inputText: themeTokens.colors.light.text,
      buttonPrimary: themeTokens.colors.light.accent,
      buttonSecondary: themeTokens.colors.light.surfaceAlt,
    },
  },

  dark: {
    // Backgrounds
    background: themeTokens.colors.dark.background,
    surface: themeTokens.colors.dark.surface,
    surfaceTransparent: alpha(themeTokens.colors.dark.surface, 0.8),
    surfaceHover: themeTokens.colors.dark.surfaceAlt,
    transparentBackground: themeTokens.colors.dark.transparentBackground,
    transparentBackgroundHover: themeTokens.colors.dark.transparentBackgroundHover,

    // Cards
    cardBackground: rgba(31, 41, 55, 1), // #1F2937 - Matches sidebarBackground
    cardBackgroundHover: rgba(55, 65, 81, 1), // #374151 - Slightly lighter for hover
    codeBackground: rgba(17, 24, 39, 0.8),
    menuBackground: rgba(0, 0, 0, 0.8),
    menuHover: rgba(0, 0, 0, 0.9),

    // Text Hierarchy
    textHeading: themeTokens.colors.dark.textHeading,
    textBody: themeTokens.colors.dark.textBody,
    textDescription: themeTokens.colors.dark.textDescription,
    textDisabled: themeTokens.colors.dark.textDisabled,
    textLink: themeTokens.colors.dark.textLink,
    textError: themeTokens.colors.dark.textError,

    // Legacy Text (for backward compatibility)
    textPrimary: themeTokens.colors.dark.text,
    textSecondary: themeTokens.colors.dark.textSecondary,
    textMuted: themeTokens.colors.dark.textMuted,
    textBrand: themeTokens.colors.dark.brandMuted,
    textBrandHover: themeTokens.colors.dark.brand,

    // Interactive
    actionDefault: themeTokens.colors.dark.textMuted,
    actionHover: themeTokens.colors.dark.text,
    accent: themeTokens.colors.dark.accent,
    accentHover: themeTokens.colors.dark.accentHover,

    // Action Menu
    actionMenu: {
      background: themeTokens.colors.dark.actionMenuBackground,
      icon: themeTokens.colors.dark.textSecondary,
      iconHover: themeTokens.colors.dark.text,
      useAction: themeTokens.colors.dark.actionMenuUseHover,
      openAction: themeTokens.colors.dark.actionMenuOpenHover,
      editAction: themeTokens.colors.dark.actionMenuEditHover,
      deleteAction: themeTokens.colors.dark.actionMenuDeleteHover,
    },

    // Buttons
    buttonPrimary: themeTokens.colors.dark.buttonPrimary,
    buttonPrimaryHover: themeTokens.colors.dark.buttonPrimaryHover,
    buttonPrimaryText: themeTokens.colors.dark.buttonPrimaryText,
    buttonSecondary: themeTokens.colors.dark.buttonSecondary,
    buttonSecondaryHover: themeTokens.colors.dark.buttonSecondaryHover,
    buttonSecondaryText: themeTokens.colors.dark.buttonSecondaryText,
    buttonDisabled: themeTokens.colors.dark.buttonDisabled,

    // Borders
    border: themeTokens.colors.dark.border,
    borderTransparent: alpha(themeTokens.colors.dark.border, 0.5),

    // Special
    folderIcon: '#FFFFFF', // White for dark mode,

    // Settings
    settings: {
      background: themeTokens.colors.dark.background,
      surfacePrimary: themeTokens.colors.dark.surface,
      surfaceSecondary: themeTokens.colors.dark.surfaceAlt,
      inputBackground: themeTokens.colors.dark.surfaceAlt,
      inputBorder: themeTokens.colors.dark.border,
      inputText: themeTokens.colors.dark.text,
      tabBackground: themeTokens.colors.dark.surfaceAlt,
      tabHover: themeTokens.colors.dark.surface,
      tabActive: themeTokens.colors.dark.accent,
      tabBorder: themeTokens.colors.dark.border,
      text: themeTokens.colors.dark.settingsText,
      description: themeTokens.colors.dark.settingsTextSecondary,
      toggleEnabled: themeTokens.colors.dark.toggleEnabled,
      toggleDisabled: themeTokens.colors.dark.toggleDisabled,
      toggleBorder: themeTokens.colors.dark.toggleBorder,
      toggleRing: themeTokens.colors.dark.toggleRing,
    },

    // Forms
    form: {
      background: themeTokens.colors.dark.background,
      modalOverlay: alpha(themeTokens.colors.dark.background, 0.8),
      cardBackground: themeTokens.colors.dark.surface,
      inputBackground: themeTokens.colors.dark.surfaceAlt,
      inputBorder: themeTokens.colors.dark.border,
      inputText: themeTokens.colors.dark.text,
      buttonPrimary: themeTokens.colors.dark.accent,
      buttonSecondary: themeTokens.colors.dark.surfaceAlt,
    },
  },

  spotify: {
    // Backgrounds
    background: themeTokens.colors.spotify.background,
    surface: themeTokens.colors.spotify.surface,
    surfaceTransparent: alpha(themeTokens.colors.spotify.surface, 0.8),
    surfaceHover: themeTokens.colors.spotify.surfaceAlt,
    transparentBackground: themeTokens.colors.spotify.transparentBackground,
    transparentBackgroundHover: themeTokens.colors.spotify.transparentBackgroundHover,

    // Cards
    cardBackground: rgba(40, 40, 40, 0.8),
    cardBackgroundHover: rgba(45, 45, 45, 0.8),
    codeBackground: rgba(30, 30, 30, 0.8),
    menuBackground: rgba(20, 20, 20, 0.9),
    menuHover: rgba(30, 30, 30, 0.9),

    // Text Hierarchy
    textHeading: themeTokens.colors.spotify.textHeading,
    textBody: themeTokens.colors.spotify.textBody,
    textDescription: themeTokens.colors.spotify.textDescription,
    textDisabled: themeTokens.colors.spotify.textDisabled,
    textLink: themeTokens.colors.spotify.textLink,
    textError: themeTokens.colors.spotify.textError,

    // Legacy Text (for backward compatibility)
    textPrimary: themeTokens.colors.spotify.text,
    textSecondary: themeTokens.colors.spotify.textSecondary,
    textMuted: themeTokens.colors.spotify.textMuted,
    textBrand: themeTokens.colors.spotify.brand,
    textBrandHover: themeTokens.colors.spotify.brandHover,

    // Interactive
    actionDefault: themeTokens.colors.spotify.textSecondary,
    actionHover: themeTokens.colors.spotify.text,
    accent: themeTokens.colors.spotify.accent,
    accentHover: themeTokens.colors.spotify.accentHover,

    // Action Menu
    actionMenu: {
      background: themeTokens.colors.spotify.actionMenuBackground,
      icon: themeTokens.colors.spotify.textSecondary,
      iconHover: themeTokens.colors.spotify.text,
      useAction: themeTokens.colors.spotify.actionMenuUseHover,
      openAction: themeTokens.colors.spotify.actionMenuOpenHover,
      editAction: themeTokens.colors.spotify.actionMenuEditHover,
      deleteAction: themeTokens.colors.spotify.actionMenuDeleteHover,
    },

    // Buttons
    buttonPrimary: themeTokens.colors.spotify.buttonPrimary,
    buttonPrimaryHover: themeTokens.colors.spotify.buttonPrimaryHover,
    buttonPrimaryText: themeTokens.colors.spotify.buttonPrimaryText,
    buttonSecondary: themeTokens.colors.spotify.buttonSecondary,
    buttonSecondaryHover: themeTokens.colors.spotify.buttonSecondaryHover,
    buttonSecondaryText: themeTokens.colors.spotify.buttonSecondaryText,
    buttonDisabled: themeTokens.colors.spotify.buttonDisabled,

    // Borders
    border: themeTokens.colors.spotify.border,
    borderTransparent: alpha(themeTokens.colors.spotify.border, 0.5),

    // Special
    folderIcon: '#1DB954', // Spotify green,

    // Settings
    settings: {
      background: themeTokens.colors.spotify.background,
      surfacePrimary: themeTokens.colors.spotify.surface,
      surfaceSecondary: themeTokens.colors.spotify.surfaceAlt,
      inputBackground: themeTokens.colors.spotify.surfaceAlt,
      inputBorder: themeTokens.colors.spotify.border,
      inputText: themeTokens.colors.spotify.text,
      tabBackground: themeTokens.colors.spotify.surfaceAlt,
      tabHover: themeTokens.colors.spotify.surface,
      tabActive: themeTokens.colors.spotify.accent,
      tabBorder: themeTokens.colors.spotify.border,
      text: themeTokens.colors.spotify.settingsText,
      description: themeTokens.colors.spotify.settingsTextSecondary,
      toggleEnabled: themeTokens.colors.spotify.toggleEnabled,
      toggleDisabled: themeTokens.colors.spotify.toggleDisabled,
      toggleBorder: themeTokens.colors.spotify.toggleBorder,
      toggleRing: themeTokens.colors.spotify.toggleRing,
    },

    // Forms
    form: {
      background: themeTokens.colors.spotify.background,
      modalOverlay: alpha(themeTokens.colors.spotify.background, 0.8),
      cardBackground: themeTokens.colors.spotify.surface,
      inputBackground: themeTokens.colors.spotify.surfaceAlt,
      inputBorder: themeTokens.colors.spotify.border,
      inputText: themeTokens.colors.spotify.text,
      buttonPrimary: themeTokens.colors.spotify.accent,
      buttonSecondary: themeTokens.colors.spotify.surfaceAlt,
    },
  },
} as const;

export type SemanticColors = typeof semanticColors;
export type SemanticColorName = keyof SemanticColors[ThemeName];
export { type ThemeName };
